import Container from '@/components/base/Container';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Seo from '@/components/feature/Seo';
import SubscribeForm from '@/components/feature/SubscribeForm';
import { principles, testimonials } from '@/mocks/testimonials';
import { siteInfo, stats } from '@/mocks/site';

export default function About() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于看料社',
    description: '看料社是一个专注娱乐热点与网络话题的追踪内容站点，强调来源可追溯与时间线完整。',
    inLanguage: 'zh-CN',
    url: `${origin}/about`,
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.intro,
    email: siteInfo.email,
    foundingDate: siteInfo.since,
  };

  const contactJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系看料社',
    inLanguage: 'zh-CN',
    url: `${origin}/about#contact`,
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: origin },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${origin}/about` },
    ],
  };

  return (
    <>
      <Seo
        title="关于我们 - 看料社的内容原则与联系方式"
        description="了解看料社的网站定位、发展理念与内容原则。我们强调来源可追溯、时间线完整、不做主观推定，并提供线索补充与更新订阅的联系方式。"
        keywords="关于看料社,内容原则,网站定位,联系方式,线索补充"
        jsonLd={[aboutJsonLd, orgJsonLd, contactJsonLd, breadcrumbJsonLd]}
      />

      <section className="border-b border-background-200 bg-background-100 py-8 md:py-12">
        <Container>
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">关于我们</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">{siteInfo.intro}</p>
        </Container>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
            <div className="relative h-64 w-full overflow-hidden rounded-lg md:h-[380px]">
              <img
                src="https://readdy.ai/api/search-image?query=Editorial%20flat%20lay%20of%20a%20wooden%20desk%20with%20notebook%2C%20pen%20and%20warm%20amber%20lamp%20light%2C%20deep%20crimson%20accents%2C%20minimal%20composition%2C%20high%20detail%2C%20no%20text%2C%20soft%20natural%20shadows&width=1200&height=900&seq=kl-about-editorial&orientation=landscape"
                alt="看料社内容整理工作场景"
                title="看料社 - 来源优先的内容整理方式"
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-label uppercase tracking-[0.22em] text-primary-600">
                <span className="h-px w-6 bg-primary-500"></span>
                Positioning
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位</h2>
              <div className="mt-5 flex flex-col gap-4 text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8">
                <p>
                  我们不追求第一时间给出一条结论，而是把一条线索的每一次变化都记下来。当你在几天后想回顾某件事的来龙去脉，还能在同一页看到完整过程。
                </p>
                <p>
                  站内内容基于公开发布的信息整理，包括官方通稿、平台公告与当事人说明。对于仅有流传版本的说法，我们会明确标注来源状态，不把它当作事实。
                </p>
                <p>
                  从 {siteInfo.since} 年开始持续整理至今，我们更在意内容是否经得起回看，而不是一时的热闹。
                </p>
              </div>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-2 gap-6 border-t border-background-200 pt-10 lg:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label}>
                <p className="font-heading text-2xl font-bold text-primary-700 md:text-3xl">{item.value}</p>
                <p className="mt-1 text-xs text-foreground-600 md:text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <Container>
          <div className="max-w-2xl">
            <p className="mb-2 flex items-center gap-2 text-xs font-label uppercase tracking-[0.22em] text-primary-600">
              <span className="h-px w-6 bg-primary-500"></span>
              Principles
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              这四条原则决定了我们怎么筛选信息、怎么组织页面，也决定了哪些内容我们不会收录。
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-primary-700">
                  <i className={`${item.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <Container>
          <div className="max-w-2xl">
            <p className="mb-2 flex items-center gap-2 text-xs font-label uppercase tracking-[0.22em] text-primary-600">
              <span className="h-px w-6 bg-primary-500"></span>
              Readers
            </p>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.content}</blockquote>
                <figcaption className="mt-5 border-t border-background-200 pt-4">
                  <p className="font-heading text-sm font-bold text-foreground-950">{item.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{item.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </Container>
      </section>

      <section id="contact" className="bg-background-100 py-14 md:py-20">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-label uppercase tracking-[0.22em] text-primary-600">
                <span className="h-px w-6 bg-primary-500"></span>
                Contact
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">补充线索与联系我们</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-[15px]">
                如果你发现了值得跟进的公开来源，或者想指出我们记录中的疏漏，都欢迎告诉我们。编辑会先核对来源是否可追溯，再决定是否进入时间线。
              </p>

              <dl className="mt-8 flex flex-col gap-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <dt className="text-xs text-foreground-500">联系邮箱</dt>
                    <dd className="mt-0.5 text-sm font-medium text-foreground-900">{siteInfo.email}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <dt className="text-xs text-foreground-500">回复时间</dt>
                    <dd className="mt-0.5 text-sm font-medium text-foreground-900">{siteInfo.workingHours}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-information-line text-lg"></i>
                  </span>
                  <div>
                    <dt className="text-xs text-foreground-500">友情提示</dt>
                    <dd className="mt-0.5 text-sm text-foreground-700">
                      请提供可公开查证的来源链接，未标注来源的信息我们通常无法收录。
                    </dd>
                  </div>
                </div>
              </dl>
            </div>

            <SubscribeForm />
          </div>
        </Container>
      </section>
    </>
  );
}